// ---------------------------------------------------------------------------
// Project → App Graph ETL.
//
// Pure, deterministic, no-side-effects conversion of `LoadedApp` (the store
// shape held in `studio-store.currentProject`) into an
// `AppGraph` of {nodes, edges}. Phase 1 covers the core dependency spine:
//
//   App ─ hasPage ─▶ Page ─ hasBlock ─▶ Block ─ usesAtom ─▶ DsAtom
//                                              └─ hasFlow ─▶ Flow
//                                              └─ bindsVar ─▶ StateVar
//                                              └─ readsSource ─▶ DataSource
//
// Deeper edges (flow → flow call graph, event → flow triggers) are left for
// Phase 2. The function is synchronous and < 5 ms for typical apps — we walk
// the ui tree once per block and bucket atoms into a Set.
// ---------------------------------------------------------------------------

import type { LoadedApp } from '@/features/page-editor/store/types'
import type { AppGraph, AppGraphEdge, AppGraphNode } from '../types'

interface UiElementLike {
  id?: string
  type?: string
  children?: UiElementLike[]
}

/** Walk a ui tree and collect unique element `type` values. */
function collectAtomTypes(root: unknown, into: Set<string>): void {
  if (!root || typeof root !== 'object') return
  const node = root as UiElementLike
  if (typeof node.type === 'string' && node.type.length > 0) {
    into.add(node.type)
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) collectAtomTypes(child, into)
  }
}

/**
 * Build a {nodes, edges} graph from a loaded project. Safe to call with
 * `null` or a truthy object that is not a `LoadedApp` (no `manifest`) —
 * returns an empty graph. The map page's `useMemo` runs before the
 * `if (!currentProject)` empty-state guard, so this must not throw on a
 * partial store value.
 */
export function projectToGraph(project: LoadedApp | null): AppGraph {
  if (!project?.manifest) return { nodes: [], edges: [] }

  const nodes: AppGraphNode[] = []
  const edges: AppGraphEdge[] = []
  const seen = new Set<string>()

  const addNode = (node: AppGraphNode) => {
    if (seen.has(node.id)) return
    seen.add(node.id)
    nodes.push(node)
  }
  const addEdge = (
    source: string,
    target: string,
    kind: AppGraphEdge['kind'],
  ) => {
    edges.push({ id: `${kind}:${source}→${target}`, source, target, kind })
  }

  const { manifest, blocks, flows } = project

  // ── App root ─────────────────────────────────────────────────
  const appId = `app:${manifest.appId ?? manifest.name ?? 'app'}`
  addNode({
    id: appId,
    type: 'app',
    label: manifest.name ?? manifest.appId ?? 'App',
    meta: {
      appId: manifest.appId,
      version: manifest.version,
      pages: manifest.pages?.length ?? 0,
    },
  })

  // ── DataSources ──────────────────────────────────────────────
  const datasources = manifest.datasources ?? {}
  for (const [name] of Object.entries(datasources)) {
    addNode({
      id: `datasource:${name}`,
      type: 'datasource',
      label: name,
    })
  }

  // ── Global state vars ────────────────────────────────────────
  const globalState = manifest.globalState ?? {}
  for (const [name] of Object.entries(globalState)) {
    addNode({
      id: `var:${name}`,
      type: 'stateVar',
      label: name,
      meta: { scope: 'global' },
    })
  }

  // ── Pages ────────────────────────────────────────────────────
  for (const page of manifest.pages ?? []) {
    const pageId = `page:${page.id}`
    addNode({
      id: pageId,
      type: 'page',
      label: page.name ?? page.title ?? page.id,
      meta: {
        route: page.route,
        layout: page.layout,
        blocks: page.blocks?.length ?? 0,
      },
    })
    addEdge(appId, pageId, 'hasPage')

    // Page → Block edges. The block may or may not be in `blocks` map
    // (e.g. lazy-loaded) — we still create the node so the graph is
    // complete; the Node Detail panel will show "unloaded" for the gap.
    for (const blockName of page.blocks ?? []) {
      const blockId = `block:${blockName}`
      const blockDef = blocks[blockName]
      const alreadySeen = seen.has(blockId)
      if (!alreadySeen) {
        addNode({
          id: blockId,
          type: 'block',
          label: blockName,
          meta: blockDef
            ? {
                blockType:
                  (blockDef.meta as unknown as { blockType?: string } | undefined)
                    ?.blockType,
                loaded: true,
              }
            : { loaded: false },
        })
      }
      addEdge(pageId, blockId, 'hasBlock')
    }
  }

  // ── Block-scoped edges: atoms, flows, vars, sources ──────────
  for (const [blockName, blockDef] of Object.entries(blocks ?? {})) {
    const blockId = `block:${blockName}`
    if (!seen.has(blockId)) {
      // Orphan block (declared but not referenced from any page).
      addNode({
        id: blockId,
        type: 'block',
        label: blockName,
        meta: { orphan: true, loaded: true },
      })
    }

    // Atoms from the ui tree. `ui` can be either a UiTree wrapper with
    // `{ root: UiElement }` shape OR a bare UiElement — walk whichever.
    const atoms = new Set<string>()
    const ui: unknown = (blockDef as unknown as { ui?: { root?: unknown } })?.ui
    if (ui && typeof ui === 'object') {
      collectAtomTypes((ui as { root?: unknown }).root ?? ui, atoms)
    }
    for (const atomType of atoms) {
      const atomId = `atom:${atomType}`
      addNode({ id: atomId, type: 'atom', label: atomType })
      addEdge(blockId, atomId, 'usesAtom')
    }

    // Flows defined on the block.
    const blockFlowMap = (blockDef as unknown as { flow?: Record<string, unknown> })?.flow
    if (blockFlowMap) {
      for (const flowName of Object.keys(blockFlowMap)) {
        const flowId = `flow:${blockName}.${flowName}`
        addNode({
          id: flowId,
          type: 'flow',
          label: `${blockName}.${flowName}`,
          meta: { scope: 'block', block: blockName },
        })
        addEdge(blockId, flowId, 'hasFlow')
      }
    }

    // State vars declared on the block.
    const blockState = (blockDef as unknown as { state?: Record<string, unknown> })?.state
    if (blockState) {
      for (const varName of Object.keys(blockState)) {
        const varId = `var:${blockName}.${varName}`
        addNode({
          id: varId,
          type: 'stateVar',
          label: `${blockName}.${varName}`,
          meta: { scope: 'block', block: blockName },
        })
        addEdge(blockId, varId, 'bindsVar')
      }
    }

    // Data sources read by the block.
    const blockData = (blockDef as unknown as { data?: Record<string, unknown> })?.data
    if (blockData) {
      for (const sourceName of Object.keys(blockData)) {
        const sourceId = `datasource:${sourceName}`
        if (!seen.has(sourceId)) {
          addNode({ id: sourceId, type: 'datasource', label: sourceName })
        }
        addEdge(blockId, sourceId, 'readsSource')
      }
    }
  }

  // ── Top-level flows (app.flows map, if present) ──────────────
  for (const flowName of Object.keys(flows ?? {})) {
    const flowId = `flow:${flowName}`
    addNode({
      id: flowId,
      type: 'flow',
      label: flowName,
      meta: { scope: 'app' },
    })
    addEdge(appId, flowId, 'hasFlow')
  }

  return { nodes, edges }
}
